import React from "react";
import { AxiosError } from "axios";
import { GetServerSideProps } from "next";
import { useRouter } from "next/router";
import Head from "next/head";
import { Box } from "@mui/material";
import { ProfileData } from "@livepix/sdk-js/types/profile";
import ProfileButton from "@livepix/components/profile/ProfileButton";
import { isUsernameValid } from "@livepix/sdk-js/validation";
import { Reward, SubscriptionPlan } from "@livepix/sdk-js/types/core";
import SubscriptionPlanSlide from "components/profile/SubscriptionPlanSlide";
import RewardView from "components/reward/RewardView";
import ProfilePage from "components/profile/ProfilePage";
import webserviceClient from "lib/webserviceClient";

type Props = {
  profile: ProfileData;
  reward: Reward | null;
  plan: SubscriptionPlan | null;
};

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
  if (!context.params?.username || !context.params?.slug) return { notFound: true };

  const { username, slug } = context.params;

  if (!username || typeof username !== "string" || !isUsernameValid(username)) return { notFound: true };
  if (!slug || typeof slug !== "string") return { notFound: true };

  try {
    const [
      { data: profile },
      {
        data: { reward, plan, advertisement },
      },
    ] = await Promise.all([
      webserviceClient.get(`/profile/${(username as string).toLowerCase()}`),
      webserviceClient.get(`/profile/${(username as string).toLowerCase()}/content/${slug}`),
    ]);

    if (!reward && !plan && !advertisement) {
      return { notFound: true };
    }

    if (advertisement) {
      return { redirect: { permanent: false, destination: advertisement.url } };
    }

    return { props: { profile, reward: reward || null, plan: plan || null } };
  } catch (e) {
    if ((e as AxiosError).response?.status !== 404) {
      throw e;
    }

    return { notFound: true };
  }
};

export default function RewardPage({ profile, reward, plan }: Props) {
  const router = useRouter();

  if (reward) {
    return (
      <>
        <Head>
          <title>{`${reward.title} de ${profile.name} - LivePix`}</title>
          <meta name="description" content={reward.description} />
          <link rel="canonical" href={`https://livepix.gg/${profile.username.toLowerCase()}/${reward.slug}`} />
        </Head>
        <ProfilePage profile={profile} width="550px">
          <Box width="100%" marginTop="20px" marginBottom="20px">
            <RewardView
              type={reward.type}
              title={reward.title}
              description={reward.description}
              content={reward.content}
              triggers={reward.triggers}
              amount={reward.amount}
            />
          </Box>
          <ProfileButton
            customcolor={profile.color}
            onClick={async () => {
              if (reward.triggers.includes("subscription")) {
                if (reward.plans && reward.plans.length > 0) {
                  await router.push(`/${profile.username}/assinatura?plan=${reward.plans.pop()}`);
                } else {
                  await router.push(`/${profile.username}/assinatura?amount=${reward.amount}`);
                }
              } else {
                await router.push(`/${profile.username}?amount=${reward.amount}`);
              }
            }}
            fullWidth
          >
            Quero participar
          </ProfileButton>
        </ProfilePage>
      </>
    );
  }

  if (plan) {
    return (
      <>
        <Head>
          <title>{`Plano ${plan.name} de ${profile.name} - LivePix`}</title>
          <meta name="description" content={plan.description} />
          <link rel="canonical" href={`https://livepix.gg/${profile.username.toLowerCase()}/${plan.slug}`} />
        </Head>
        <ProfilePage profile={profile}>
          <Box width="100%" marginTop="20px" marginBottom="20px">
            <SubscriptionPlanSlide profile={profile} plan={plan} />
          </Box>
        </ProfilePage>
      </>
    );
  }

  return null;
}
